import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarBlank, Camera, X } from '@phosphor-icons/react';

import { paths } from '../../routes/paths';
import pt from '../../locales/pt';
import { cn } from '../../utils/cn';
import { dismissAlertId, readDismissedAlertIds } from '../../utils/dismissedAlerts';
import { monthLongLabel, resolveSelfieInviteState } from '../../lib/selfieDisplayUtils';

function format(template, params) {
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    params[key] === undefined ? `{${key}}` : String(params[key]),
  );
}

/**
 * .pen: pk3I7 (--overdue, gradiente warning), onErA (--due, sage-solid),
 * aLDdy (--upcoming, cartão neutro sem CTA/dismiss).
 */
export function SelfieInviteBanner({ currentCycle, className }) {
  const state = resolveSelfieInviteState(currentCycle);
  const dismissKey =
    state.kind === 'due' || state.kind === 'overdue'
      ? `selfie-invite-${state.referenceMonth}`
      : null;
  const [justDismissed, setJustDismissed] = useState(false);
  const dismissed =
    justDismissed || (Boolean(dismissKey) && readDismissedAlertIds().has(dismissKey));

  if ((state.kind === 'due' || state.kind === 'overdue') && !dismissed) {
    const isOverdue = state.kind === 'overdue';
    const month = monthLongLabel(state.referenceMonth).toLowerCase();
    const when = isOverdue
      ? pt.snapshots.history.invite.overdue
      : pt.snapshots.history.invite.dueToday;

    return (
      <div
        data-ds-id={isOverdue ? 'ds/banner/selfie-invite--overdue' : 'ds/banner/selfie-invite--due'}
        className={cn(
          'flex items-center gap-space-3 rounded-ds-xl px-space-4 py-space-3 shadow-[0_6px_16px] shadow-glass-shadow',
          isOverdue
            ? 'bg-gradient-to-br from-warning-gradient-hero-dark-from to-warning-gradient-hero-to'
            : 'bg-sage-solid',
          className,
        )}
      >
        <Link
          to={paths.selfieNew}
          className="flex min-w-0 flex-1 items-center gap-space-3 text-on-hero transition"
        >
          <span className="flex size-[42px] shrink-0 items-center justify-center rounded-ds-md bg-on-hero-track">
            <Camera className="size-5 text-on-hero" weight="regular" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-display text-type-body font-bold leading-[1.3] text-on-hero">
              {format(
                isOverdue
                  ? pt.snapshots.history.invite.overdueTitle
                  : pt.snapshots.history.invite.dueTitle,
                { month },
              )}
            </p>
            <p className="mt-0.5 text-type-chip leading-[1.35] text-on-hero-muted">
              {format(pt.snapshots.history.invite.dueSubtitle, { when })}
            </p>
          </div>
          <span
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-ds-full bg-on-hero',
              isOverdue ? 'text-warning-fg' : 'text-sage-700',
            )}
          >
            <ArrowRight className="size-4" weight="bold" aria-hidden />
          </span>
        </Link>
        <button
          type="button"
          onClick={() => {
            dismissAlertId(dismissKey);
            setJustDismissed(true);
          }}
          className="shrink-0 text-on-hero-muted hover:text-on-hero"
          aria-label={pt.alerts.dismiss}
        >
          <X className="size-3.5" aria-hidden />
        </button>
      </div>
    );
  }

  if (state.kind === 'upcoming') {
    const subtitle =
      state.daysUntil === 1
        ? pt.snapshots.history.invite.upcomingSubtitleOne
        : format(pt.snapshots.history.invite.upcomingSubtitleMany, { days: state.daysUntil });

    return (
      <div
        data-ds-id="ds/banner/selfie-invite--upcoming"
        className={cn(
          'flex items-center gap-space-3 rounded-ds-xl border border-border bg-card px-space-4 py-space-3',
          className,
        )}
      >
        <span className="flex size-9 shrink-0 items-center justify-center rounded-ds-md bg-muted">
          <CalendarBlank className="size-[18px] text-muted-foreground" weight="regular" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-type-body-sm font-semibold leading-[1.35] text-card-foreground">
            {format(pt.snapshots.history.invite.upcoming, { date: state.nextLabel })}
          </p>
          <p className="text-type-chip leading-[1.35] text-muted-foreground">{subtitle}</p>
        </div>
      </div>
    );
  }

  return null;
}

export default SelfieInviteBanner;
